import { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import type { FC, ReactNode } from 'react';
import { apiClient } from '../utils/api-client';
import { useAuth } from './AuthContext';

export interface UserSettings {
  language: string;
  sourceLanguage: string;
  cefrLevel: string;
  theme: 'light' | 'dark';
  audioVoice: string;
  audioSpeed: number;
  imageStyle: string;
}

const DEFAULT_SETTINGS: UserSettings = {
  language: 'en',
  sourceLanguage: 'es',
  cefrLevel: 'B1',
  theme: 'light',
  audioVoice: 'female',
  audioSpeed: 1.0,
  imageStyle: 'Oil painting',
};

interface SettingsContextType {
  settings: UserSettings;
  loading: boolean;
  updateSettings: (updates: Partial<UserSettings>) => Promise<void>;
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

export const SettingsProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { isSessionValid } = useAuth();
  const [settings, setSettings] = useState<UserSettings>(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [settingsExist, setSettingsExist] = useState(false);

  useEffect(() => {
    if (!isSessionValid) {
      setLoading(false);
      return;
    }

    const fetchSettings = async () => {
      try {
        const existsResponse = await apiClient.get<{ exists: boolean }>('/reader/user/settings/exists');
        if (existsResponse.exists) {
          const data = await apiClient.get<{ settings: UserSettings }>('/reader/user/settings');
          setSettings(data.settings);
          setSettingsExist(true);
        }
      } catch (err) {
        console.error('Failed to fetch settings:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, [isSessionValid]);

  const updateSettings = useCallback(async (updates: Partial<UserSettings>) => {
    const newSettings = { ...settings, ...updates };
    setSettings(newSettings);

    try {
      if (settingsExist) {
        await apiClient.patch('/reader/user/settings', newSettings);
      } else {
        await apiClient.post('/reader/user/settings', newSettings);
        setSettingsExist(true);
      }
    } catch (err) {
      console.error('Failed to save settings:', err);
      throw err;
    }
  }, [settings, settingsExist]);

  const value = useMemo(
    () => ({ settings, loading, updateSettings }),
    [settings, loading, updateSettings],
  );

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};
